import { AxiosError, AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import React from 'react'
import useSWR, { SWRConfiguration } from 'swr'

/**
 * A hook for querying Rolodex entries using GraphQL.
 * Uses SWR behind the scenes for caching and revalidation.
 *
 * @param query - The GraphQL query string
 * @param options - SWR configuration options
 *
 * @example
 * ```
 * const { data, isLoading, error } = useRolodexGraphQL(`
 *   {
 *     organization(limit: 10) {
 *       id
 *       name
 *       site(limit: 5) {
 *         id
 *         name
 *       }
 *     }
 *   }
 * `)
 * ```
 */

type RolodexGraphQLResponse = {
  // This differs from the dispenser used in other Rolodex hooks in that, objects may be nested, and also, each object may only have a limited set of properties depending on the query itself.
  // We'll need to come up with a way to type it, with types likely being generated based on the query itself.
  data?: unknown
  isLoading: boolean
  isValidating: boolean
  hasEverLoaded: boolean
  error: AxiosError | undefined
  forceRefresh: () => Promise<void>
}

export const useRolodexGraphQL = (
  // In the medium term, we'll ideally store the queries each in separate files and add type generation on an individual basis for each query.
  query: string | undefined,
  options?: SWRConfiguration
): RolodexGraphQLResponse => {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = React.useContext(RolodexContext)

  const swrKey = React.useMemo(() => {
    if (!query || !isAuthenticated) return null
    return [apiURL, 'graphql', query]
  }, [query, isAuthenticated, apiURL])

  const fetcher = React.useCallback(
    async ([url, , graphqlQuery]: [
      string,
      string,
      string,
    ]): Promise<unknown> => {
      const response: AxiosResponse = await axios.post(
        `${url}/api/v2/entries/graphql`,
        graphqlQuery,
        {
          headers: {
            'Content-Type': 'text/plain',
          },
        }
      )
      return response.data
    },
    [axios]
  )

  const { data, isLoading, isValidating, error, mutate } = useSWR<
    unknown,
    AxiosError
  >(swrKey, fetcher, options)

  const forceRefresh = React.useCallback(
    async () => await mutate().then(() => {}),
    [mutate]
  )

  return {
    data,
    isLoading: (!data && !!swrKey) || isLoading,
    isValidating,
    hasEverLoaded: !!data,
    error,
    forceRefresh,
  }
}
